import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Machine, TimelineBatch } from "../../types/mes";
import {
  absoluteMinutes,
  addCalendarDays,
  buildGanttLanes,
  clipBatchesToDay,
  formatHrDay,
  GanttGroupBy,
  GanttPhaseRef,
  ganttEpochDate,
  isDrawablePhase,
  isGanttResourceGrouping,
} from "../../domain/MES/Planning/planningUtils";
import {
  DEFAULT_PHASE_COLOR,
  LEGACY_PHASE_CODES,
  PHASE_META,
  PHASE_ORDER,
  phaseColor,
  phaseLabel,
} from "../../domain/MES/Planning/phaseDisplay";
import { clockTimeToMinutes } from "../../utils/processParameterSerialize";
import { fitSvgText, measureSvgText } from "../../utils/svgText";
import { dateLocale, t } from "../../i18n/locale";

interface GanttPlannerChartProps {
  batches: TimelineBatch[];
  selectedDate: string;
  /**
   * When set, only the work that runs on this calendar day is drawn (overnight
   * phases are clipped at midnight). Omit to let a multi-day product stretch
   * the axis across every day its phases cover.
   */
  clipToDate?: string;
  /** Overrides the default "Timeline Gantt — …" heading. */
  title?: string;
  groupBy?: GanttGroupBy;
  onGroupByChange?: (groupBy: GanttGroupBy) => void;
  onReorder?: (draggedBatchId: number, targetBatchId: number) => void;
  /** Machine catalogue, used to name the lanes of the "po stroju" grouping. */
  machines?: Machine[];
  /** Selected batch ids. Omit to let the chart keep its own selection. */
  selectedBatchIds?: number[];
  onSelectionChange?: (batchIds: number[]) => void;
}

const GROUP_OPTIONS: { id: GanttGroupBy; label: string; title: string }[] = [
  {
    id: "machine",
    label: "Po stroju",
    title: "Traka je stroj, a svaka faza stoji na stroju koji ju izvodi",
  },
  {
    id: "phase",
    label: "Po fazi",
    title: "Traka je faza proizvodnje, pa se vidi koliko se zamjesa preklapa u istoj fazi",
  },
  {
    id: "mixer",
    label: "Po mikseru",
    title: "Traka je mikser, a cijeli lanac faza zamjese crta se u njegovom redu",
  },
  {
    id: "oven",
    label: "Po peći",
    title: "Traka je peć, a cijeli lanac faza zamjese crta se u njenom redu",
  },
];

/**
 * Fonts of the two label-column texts, kept in sync with their `<text>` props and
 * with the app font stack ($font-sans) the SVG inherits.
 */
const LABEL_FONT_FAMILY = "Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const BATCH_LABEL_FONT = `11px ${LABEL_FONT_FAMILY}`;
const LANE_LABEL_FONT = `600 15px ${LABEL_FONT_FAMILY}`;
/** Font of the text drawn inside a bar, used to fit the batch code into it. */
const BAR_TEXT_FONT = `600 9px ${LABEL_FONT_FAMILY}`;
const LABEL_PADDING_LEFT = 15;
const LABEL_PADDING_RIGHT = 12;
const MIN_LABEL_WIDTH = 175;
/** Beyond this the label column would eat too much of the time axis. */
const MAX_LABEL_WIDTH = 340;

const SINGLE_DAY_HEADER_HEIGHT = 40;
const MULTI_DAY_HEADER_HEIGHT = 56;
const LANE_HEADER_HEIGHT = 50;
const BAR_HEIGHT = 20;
/** Vertical gap between the stacked bars of parallel phases. */
const BAR_GAP = 4;
const ROW_PADDING = 10;

const SELECT_BOX_SIZE = 11;
const SELECT_BOX_GAP = 8;
/** Horizontal room the selection checkbox takes from the batch label. */
const SELECT_BOX_WIDTH = SELECT_BOX_SIZE + SELECT_BOX_GAP;

const SELECTION_COLOR = "#4a90e2";

const TOOLTIP_WIDTH = 270;
const TOOLTIP_OFFSET = 14;
/** Rough height used only to decide whether to flip the panel above the cursor. */
const TOOLTIP_MAX_HEIGHT = 190;

type PhaseBar = {
  code: string;
  /** Batch the segment belongs to — a resource row mixes several. */
  batch: TimelineBatch;
  /** The segment itself, for the hover details. */
  phase: GanttPhaseRef;
  start: number;
  end: number;
  color: string;
  label: string;
};

/** A bar placed on a sub-lane inside its row. */
type PlacedBar = PhaseBar & { subLane: number };

type LayoutRow = {
  key: string;
  label: string;
  /**
   * Set only on batch rows (mixer/oven groupings), where the whole row is one
   * batch and therefore clickable and draggable as a unit. Resource rows hold
   * bars of many batches, so selection happens per bar instead.
   */
  batch?: TimelineBatch;
  y: number;
  height: number;
  bars: PlacedBar[];
};

type LayoutLane = {
  laneName: string;
  /** 0 on resource lanes: the lane IS the row, its name sits in the row label. */
  headerHeight: number;
  batchCount: number;
  y: number;
  rows: LayoutRow[];
};

/**
 * Stack overlapping phases on separate sub-lanes so bars sharing a time window
 * show above each other instead of one painted over the other — a parallel
 * phase group on a batch row, or two batches contending for one machine on a
 * resource row.
 *
 * Greedy interval partitioning over start-sorted bars: a bar reuses the first
 * sub-lane already free at its start, otherwise a new sub-lane is opened. For
 * a purely sequential routing this always yields a single sub-lane, so those
 * rows keep their original height.
 */
function placeBarsOnSubLanes(bars: PhaseBar[]): { bars: PlacedBar[]; subLanes: number } {
  const subLaneEnds: number[] = [];
  const placed = [...bars]
    .sort((a, b) => a.start - b.start)
    .map((bar) => {
      let subLane = subLaneEnds.findIndex((end) => bar.start >= end);
      if (subLane === -1) {
        subLane = subLaneEnds.length;
        subLaneEnds.push(bar.end);
      } else {
        subLaneEnds[subLane] = Math.max(subLaneEnds[subLane], bar.end);
      }
      return { ...bar, subLane };
    });
  return { bars: placed, subLanes: Math.max(1, subLaneEnds.length) };
}

function rowHeightFor(subLanes: number): number {
  return subLanes * BAR_HEIGHT + (subLanes - 1) * BAR_GAP + ROW_PADDING * 2;
}

/**
 * Gantt chart with swim-lanes by machine, by production phase, or — in the two
 * batch-centric groupings — by mixer or oven.
 */
const GanttPlannerChart: React.FC<GanttPlannerChartProps> = ({
  batches,
  selectedDate,
  clipToDate,
  title,
  groupBy: controlledGroupBy,
  onGroupByChange,
  onReorder,
  machines,
  selectedBatchIds: controlledSelection,
  onSelectionChange,
}) => {
  const [internalGroupBy, setInternalGroupBy] = useState<GanttGroupBy>("machine");
  const groupBy = controlledGroupBy ?? internalGroupBy;
  const setGroupBy = onGroupByChange ?? setInternalGroupBy;

  /** Machine / phase on the y axis, one row per resource, bars from many batches. */
  const isResourceView = isGanttResourceGrouping(groupBy);

  // Reordering swaps two batches in a mixer/oven sequence. A resource row holds
  // segments of several batches and exposes no sequence to swap, so dragging
  // there would silently reorder something else.
  const canReorder = !!onReorder && !isResourceView;

  const machineNames = useMemo(
    () => new Map((machines ?? []).map((m) => [m.ID, m.MachineName])),
    [machines]
  );

  // useId() contains colons, which are unsafe inside a url(#...) reference.
  const labelClipId = `gantt-planner-label-${useId().replace(/:/g, "")}`;

  // Responsive chart width: measure the scroll container so the Gantt fills the
  // available viewport instead of a fixed 1200px (which left dead space / forced
  // horizontal scroll on laptops). Falls back to 1200 before first measurement.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(1200);
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (w && w > 0) setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [hoveredBatch, setHoveredBatch] = useState<string | null>(null);
  const [hoveredPhase, setHoveredPhase] = useState<{
    bar: PlacedBar;
    x: number;
    y: number;
  } | null>(null);
  const [draggedBatchId, setDraggedBatchId] = useState<number | null>(null);
  const [dragOverBatchId, setDragOverBatchId] = useState<number | null>(null);
  const [internalSelection, setInternalSelection] = useState<number[]>([]);

  const selectedBatchIds = controlledSelection ?? internalSelection;
  const selectedSet = new Set(selectedBatchIds);

  const applySelection = (next: number[]) => {
    if (controlledSelection === undefined) setInternalSelection(next);
    onSelectionChange?.(next);
  };

  const toggleSelection = (batchId: number) =>
    applySelection(
      selectedSet.has(batchId)
        ? selectedBatchIds.filter((id) => id !== batchId)
        : [...selectedBatchIds, batchId]
    );

  useEffect(() => {
    setDraggedBatchId(null);
    setDragOverBatchId(null);
    setHoveredPhase(null);
  }, [groupBy]);

  /**
   * Track the hovered segment and the cursor for the details panel.
   *
   * Guarded against a re-render per mouse pixel: the state is left untouched
   * while the cursor stays on the same segment and barely moves. The segment is
   * compared by its data, not by object identity, because the layout — and with
   * it every bar object — is rebuilt on each render.
   */
  const showPhaseDetails = (bar: PlacedBar, event: React.MouseEvent) => {
    const x = event.clientX;
    const y = event.clientY;
    const sameBar = (a: PlacedBar, b: PlacedBar) =>
      a.batch.ID === b.batch.ID && a.code === b.code && a.start === b.start;
    setHoveredPhase((prev) =>
      prev && sameBar(prev.bar, bar) && Math.abs(prev.x - x) < 6 && Math.abs(prev.y - y) < 6
        ? prev
        : { bar, x, y }
    );
  };

  const viewBatches = useMemo(
    () =>
      (clipToDate ? clipBatchesToDay(batches, clipToDate) : batches) as TimelineBatch[],
    [batches, clipToDate]
  );

  const epoch = ganttEpochDate(viewBatches) ?? clipToDate ?? selectedDate;

  const minutesFromEpoch = (value?: string): number | null =>
    absoluteMinutes(value, epoch) ?? clockTimeToMinutes(value);

  const minutesToClock = (minutes: number, asEnd = false): string => {
    const clock = ((minutes % 1440) + 1440) % 1440;
    if (asEnd && minutes > 0 && clock === 0) return "24:00";
    const h = Math.floor(clock / 60);
    const m = clock % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };

  const dateOfAbs = (minutes: number): string =>
    addCalendarDays(epoch, Math.floor(minutes / 1440));

  const formatDuration = (minutes: number): string => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${m} min`;
  };

  const swimLanes = buildGanttLanes(viewBatches, groupBy, machineNames);

  let minTime = Number.POSITIVE_INFINITY;
  let maxTime = 0;

  viewBatches.forEach((b) => {
    const times =
      b.Phases && b.Phases.length > 0
        ? b.Phases.flatMap((p) => [minutesFromEpoch(p.Start), minutesFromEpoch(p.End)])
        : [
            minutesFromEpoch(b.MixingStart),
            minutesFromEpoch(b.MixingEnd),
            minutesFromEpoch(b.FermentationStart),
            minutesFromEpoch(b.FermentationEnd),
            minutesFromEpoch(b.ProcessingStart),
            minutesFromEpoch(b.ProcessingEnd),
            minutesFromEpoch(b.FinalProofStart),
            minutesFromEpoch(b.FinalProofEnd),
            minutesFromEpoch(b.BakingStart),
            minutesFromEpoch(b.BakingEnd),
          ];

    times.forEach((t) => {
      if (t !== null) {
        minTime = Math.min(minTime, t);
        maxTime = Math.max(maxTime, t);
      }
    });
  });

  if (!Number.isFinite(minTime) || minTime >= maxTime) {
    minTime = 4 * 60;
    maxTime = 22 * 60;
  } else {
    minTime = Math.max(0, minTime - 30);
    maxTime = maxTime + 30;
  }

  const spanDays = Math.max(1, Math.ceil(maxTime / 1440));
  const axisHeaderHeight = spanDays > 1 ? MULTI_DAY_HEADER_HEIGHT : SINGLE_DAY_HEADER_HEIGHT;
  const timeRange = maxTime - minTime;

  const formatAbsTime = (minutes: number, asEnd = false): string => {
    const clock = minutesToClock(minutes, asEnd);
    return spanDays > 1 ? `${formatHrDay(dateOfAbs(minutes))} ${clock}` : clock;
  };

  /** Drawable bars for a batch's phase segments. */
  const phaseBarsOf = (batch: TimelineBatch, phases: GanttPhaseRef[]): PhaseBar[] =>
    phases.flatMap((p, idx) => {
      if (!isDrawablePhase(p)) return [];
      const start = minutesFromEpoch(p.start) as number;
      const end = minutesFromEpoch(p.end) as number;
      return [
        {
          code: p.code || `phase_${idx}`,
          batch,
          phase: p,
          start,
          end,
          color: phaseColor(p.code),
          label: phaseLabel(p.code),
        },
      ];
    });

  // Rows are laid out top-down with per-row heights: a row grows only when its
  // bars overlap and need extra sub-lanes.
  //
  // The two layouts differ in what a row IS. In the resource groupings the y
  // axis is the machine (or the phase) itself, so a lane collapses into a
  // single row carrying every segment that runs there. In the mixer/oven
  // groupings the y axis is the batch, grouped under a device header.
  let cursorY = axisHeaderHeight;
  const layoutLanes: LayoutLane[] = swimLanes.map((lane) => {
    const laneY = cursorY;

    if (isResourceView) {
      const { bars, subLanes } = placeBarsOnSubLanes(
        lane.rows.flatMap((laneRow) =>
          phaseBarsOf(laneRow.batch as TimelineBatch, laneRow.phases)
        )
      );
      const height = rowHeightFor(subLanes);
      cursorY += height;
      return {
        laneName: lane.laneName,
        headerHeight: 0,
        batchCount: lane.rows.length,
        y: laneY,
        rows: [{ key: lane.laneName, label: lane.laneName, y: laneY, height, bars }],
      };
    }

    cursorY += LANE_HEADER_HEIGHT;
    const rows = lane.rows.map((laneRow) => {
      const batch = laneRow.batch as TimelineBatch;
      const { bars, subLanes } = placeBarsOnSubLanes(phaseBarsOf(batch, laneRow.phases));
      const row: LayoutRow = {
        key: String(batch.ID),
        label: batch.MixCode ?? "",
        batch,
        y: cursorY,
        height: rowHeightFor(subLanes),
        bars,
      };
      cursorY += row.height;
      return row;
    });
    return {
      laneName: lane.laneName,
      headerHeight: LANE_HEADER_HEIGHT,
      batchCount: rows.length,
      y: laneY,
      rows,
    };
  });
  const totalHeight = cursorY;

  // Size the label column to the longest name it has to show. MixCode embeds the
  // whole product name, so a fixed column cut most of it off; the width is capped
  // so the time axis keeps the bulk of the chart, and anything past the cap gets
  // an ellipsis instead of being sliced mid-glyph.
  const widestLabel = layoutLanes.reduce((widest, lane) => {
    const laneLabel = lane.headerHeight > 0 ? measureSvgText(lane.laneName, LANE_LABEL_FONT) : 0;
    const rowLabel = lane.rows.reduce(
      (max, row) =>
        Math.max(
          max,
          row.batch
            ? measureSvgText(row.label, BATCH_LABEL_FONT) + SELECT_BOX_WIDTH
            : measureSvgText(row.label, LANE_LABEL_FONT)
        ),
      0
    );
    return Math.max(widest, laneLabel, rowLabel);
  }, 0);
  const labelWidth = Math.min(
    Math.min(MAX_LABEL_WIDTH, Math.max(MIN_LABEL_WIDTH, containerWidth * 0.4)),
    Math.max(MIN_LABEL_WIDTH, Math.ceil(widestLabel + LABEL_PADDING_LEFT + LABEL_PADDING_RIGHT))
  );
  const labelTextWidth = labelWidth - LABEL_PADDING_LEFT - LABEL_PADDING_RIGHT;
  // Batch rows give up the left of the label column to the selection checkbox.
  const batchLabelX = LABEL_PADDING_LEFT + SELECT_BOX_WIDTH;
  const batchTextWidth = labelWidth - batchLabelX - LABEL_PADDING_RIGHT;

  // Size the chart by CONTENT, not by the container: give the time axis a minimum
  // pixels-per-minute so hours stay legible, then let the container scroll
  // horizontally when the day is long. Still fill the container when the day is
  // short (so it never looks narrow). Fixing width to the container squished every
  // bar together on a full day — the axis must be scrollable instead.
  const MIN_PX_PER_MIN = spanDays > 1 ? 1.15 : 1.6;
  const contentWidth = labelWidth + timeRange * MIN_PX_PER_MIN;
  const chartWidth = Math.max(Math.floor(containerWidth) - 2, Math.ceil(contentWidth));

  const hourMarkers: number[] = [];
  const startHour = Math.floor(minTime / 60);
  const endHour = Math.ceil(maxTime / 60);
  for (let h = startHour; h <= endHour; h++) {
    hourMarkers.push(h * 60);
  }
  const dayBands = Array.from({ length: spanDays }, (_, i) => {
    const dayStart = i * 1440;
    const dayEnd = (i + 1) * 1440;
    return {
      date: addCalendarDays(epoch, i),
      start: Math.max(dayStart, minTime),
      end: Math.min(dayEnd, maxTime),
      boundary: dayStart,
    };
  }).filter((band) => band.end > band.start);

  const getBarX = (minutes: number) => {
    return labelWidth + ((minutes - minTime) / timeRange) * (chartWidth - labelWidth);
  };

  const getBarWidth = (start: number, end: number) => {
    return ((end - start) / timeRange) * (chartWidth - labelWidth);
  };

  // Phase codes actually present across the batches drive the legend so the
  // new phases (dividing, moulding, packaging) appear automatically.
  const usedPhaseCodes = (() => {
    const set = new Set<string>();
    viewBatches.forEach((b) => {
      if (b.Phases && b.Phases.length > 0) b.Phases.forEach((p) => set.add(p.Code));
    });
    if (set.size === 0) LEGACY_PHASE_CODES.forEach((c) => set.add(c));
    return PHASE_ORDER.filter((c) => set.has(c));
  })();

  const handleDragStart = (batchId: number) => {
    setDraggedBatchId(batchId);
  };

  const handleDragOver = (e: React.DragEvent, batchId: number) => {
    e.preventDefault();
    if (draggedBatchId !== null && draggedBatchId !== batchId) {
      setDragOverBatchId(batchId);
    }
  };

  const handleDrop = (e: React.DragEvent, targetBatchId: number) => {
    e.preventDefault();
    if (draggedBatchId !== null && draggedBatchId !== targetBatchId && onReorder) {
      onReorder(draggedBatchId, targetBatchId);
    }
    setDraggedBatchId(null);
    setDragOverBatchId(null);
  };

  const handleDragEnd = () => {
    setDraggedBatchId(null);
    setDragOverBatchId(null);
  };

  const dragHint = canReorder
    ? t("↕️ Povuci zamjesu na drugu da zamijene mjesta unutar iste trake")
    : onReorder
    ? t("↕️ Promjena redoslijeda radi u pogledu po mikseru ili po peći")
    : null;

  return (
    <div className="gantt-planner-chart">
      <div className="gantt-header">
        <div className="gantt-header-left">
          <h3>
            {title ??
              (spanDays > 1
                ? t("Timeline Gantt — {from} – {to}", {
                    from: formatHrDay(epoch),
                    to: formatHrDay(addCalendarDays(epoch, spanDays - 1)),
                  })
                : t("Timeline Gantt - {date}", {
                    date: new Date(selectedDate).toLocaleDateString(dateLocale()),
                  }))}
          </h3>
          <div className="view-toggle gantt-group-toggle">
            {GROUP_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                title={t(option.title)}
                className={groupBy === option.id ? "active" : ""}
                onClick={() => setGroupBy(option.id)}
              >
                {t(option.label)}
              </button>
            ))}
          </div>
          <div className="gantt-hints">
            {dragHint && <span className="gantt-drag-hint">{dragHint}</span>}
            <span className="gantt-drag-hint">
              {isResourceView
                ? t("Klikni na fazu da označiš njenu zamjesu u svim trakama, ponovni klik je odznačuje")
                : t("Klikni na red da ga odabereš, ponovni klik ga odznačuje")}
            </span>
          </div>
          {selectedBatchIds.length > 0 && (
            <div className="gantt-selection-bar">
              <span>{t("{count} odabrano", { count: selectedBatchIds.length })}</span>
              <button type="button" onClick={() => applySelection([])}>
                {t("Poništi odabir")}
              </button>
            </div>
          )}
        </div>
        <div className="gantt-legend">
          {usedPhaseCodes.map((code) => {
            const meta = PHASE_META[code];
            return (
              <div className="legend-item" key={code}>
                <span
                  className="legend-color"
                  style={{ background: meta?.color ?? DEFAULT_PHASE_COLOR }}
                />
                <span>{meta?.name ? t(meta.name) : code}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="gantt-scroll-container" ref={scrollContainerRef}>
        <svg width={chartWidth} height={totalHeight} className="gantt-svg">
          <defs>
            {/* Keeps long machine/batch names inside the label column instead of
                spilling over the phase bars. */}
            <clipPath id={labelClipId}>
              <rect x={0} y={0} width={labelWidth - 8} height={totalHeight} />
            </clipPath>
          </defs>
          <g className="gantt-time-axis">
            <rect x={0} y={0} width={chartWidth} height={axisHeaderHeight} fill="#1a1d29" />
            {dayBands.map((band, i) => {
              const x1 = getBarX(band.start);
              const x2 = getBarX(band.end);
              return (
                <g key={band.date}>
                  {spanDays > 1 && (
                    <>
                      <rect
                        x={x1}
                        y={axisHeaderHeight}
                        width={Math.max(0, x2 - x1)}
                        height={totalHeight - axisHeaderHeight}
                        fill={i % 2 === 0 ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.05)"}
                      />
                      {band.boundary > minTime && (
                        <line
                          x1={getBarX(band.boundary)}
                          y1={0}
                          x2={getBarX(band.boundary)}
                          y2={totalHeight}
                          stroke="rgba(232,230,227,0.22)"
                          strokeWidth={1.5}
                        />
                      )}
                      <text
                        x={(x1 + x2) / 2}
                        y={16}
                        textAnchor="middle"
                        fill="#e8e6e3"
                        fontSize="12"
                        fontWeight="600"
                      >
                        {formatHrDay(band.date)}
                      </text>
                    </>
                  )}
                </g>
              );
            })}
            {hourMarkers.map((h) => {
              const x = getBarX(h);
              return (
                <g key={h}>
                  <line
                    x1={x}
                    y1={axisHeaderHeight}
                    x2={x}
                    y2={totalHeight}
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth={1}
                    strokeDasharray="3,3"
                  />
                  <text
                    x={x}
                    y={spanDays > 1 ? 38 : 25}
                    textAnchor="middle"
                    fill="#adb3bd"
                    fontSize="13"
                    fontWeight="500"
                  >
                    {minutesToClock(h)}
                  </text>
                </g>
              );
            })}
          </g>

          {layoutLanes.map((lane) => (
            <g key={`${groupBy}-${lane.laneName}`} className="gantt-swim-lane">
              {lane.headerHeight > 0 && (
                <>
                  <rect x={0} y={lane.y} width={chartWidth} height={lane.headerHeight} fill="#252935" />
                  <rect x={0} y={lane.y} width={labelWidth} height={lane.headerHeight} fill="#2a2f3e" />
                  {/* Machine name and batch count are stacked, not side by side:
                      on one line a long name ("Diosna mala") ran into the
                      right-aligned counter. */}
                  <text
                    x={LABEL_PADDING_LEFT}
                    y={lane.y + lane.headerHeight / 2 - 3}
                    fill="#e8e6e3"
                    fontSize="15"
                    fontWeight="600"
                    clipPath={`url(#${labelClipId})`}
                  >
                    <title>{lane.laneName}</title>
                    {fitSvgText(lane.laneName, labelTextWidth, LANE_LABEL_FONT)}
                  </text>
                  <text
                    x={LABEL_PADDING_LEFT}
                    y={lane.y + lane.headerHeight / 2 + 15}
                    fill="#adb3bd"
                    fontSize="12"
                  >
                    {lane.batchCount} zamjesa
                  </text>
                </>
              )}

              {lane.rows.map((row) => {
                const rowBatch = row.batch;
                // A resource row carries several batches, so it has no single
                // hover/selection state of its own — that lives on its bars.
                const isHovered = !!rowBatch && hoveredBatch === rowBatch.MixCode;
                const isDragging = !!rowBatch && draggedBatchId === rowBatch.ID;
                const isDragOver = !!rowBatch && dragOverBatchId === rowBatch.ID;
                const isSelected = !!rowBatch && selectedSet.has(rowBatch.ID);
                const boxY = row.y + row.height / 2 - SELECT_BOX_SIZE / 2;

                const rowFill = isSelected
                  ? "rgba(74, 144, 226, 0.16)"
                  : isHovered
                  ? "rgba(255,255,255,0.05)"
                  : "transparent";

                return (
                  <g
                    key={row.key}
                    className="gantt-batch-row"
                    onMouseEnter={rowBatch ? () => setHoveredBatch(rowBatch.MixCode) : undefined}
                    onMouseLeave={rowBatch ? () => setHoveredBatch(null) : undefined}
                    onClick={rowBatch ? () => toggleSelection(rowBatch.ID) : undefined}
                    style={{
                      cursor: canReorder ? "move" : rowBatch ? "pointer" : "default",
                      opacity: isDragging ? 0.5 : 1,
                    }}
                  >
                    {canReorder && rowBatch && (
                      <foreignObject x={0} y={row.y} width={chartWidth} height={row.height}>
                        <div
                          draggable={true}
                          // The drag layer covers the label column, so the SVG
                          // <title> underneath never fires — repeat it here to
                          // keep the full name reachable on an ellipsized label.
                          title={rowBatch.MixCode}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            handleDragStart(rowBatch.ID);
                          }}
                          onDragOver={(e) => {
                            e.stopPropagation();
                            handleDragOver(e, rowBatch.ID);
                          }}
                          onDrop={(e) => {
                            e.stopPropagation();
                            handleDrop(e, rowBatch.ID);
                          }}
                          onDragEnd={handleDragEnd}
                          style={{
                            width: "100%",
                            height: "100%",
                            cursor: "move",
                            backgroundColor: isDragOver
                              ? "rgba(74, 144, 226, 0.2)"
                              : rowFill,
                            border: isDragOver ? `2px dashed ${SELECTION_COLOR}` : "none",
                          }}
                        />
                      </foreignObject>
                    )}
                    <rect
                      x={0}
                      y={row.y}
                      width={chartWidth}
                      height={row.height}
                      fill={canReorder ? "transparent" : rowFill}
                      pointerEvents={canReorder ? "none" : "auto"}
                    />

                    <rect
                      x={0}
                      y={row.y}
                      width={labelWidth}
                      height={row.height}
                      fill={isSelected ? "#223148" : rowBatch ? "#1e222e" : "#2a2f3e"}
                    />
                    {isSelected && (
                      <rect x={0} y={row.y} width={3} height={row.height} fill={SELECTION_COLOR} />
                    )}
                    {rowBatch ? (
                      <>
                        <rect
                          x={LABEL_PADDING_LEFT}
                          y={boxY}
                          width={SELECT_BOX_SIZE}
                          height={SELECT_BOX_SIZE}
                          rx={2}
                          fill={isSelected ? SELECTION_COLOR : "transparent"}
                          stroke={isSelected ? SELECTION_COLOR : "#5a6070"}
                          strokeWidth={1.5}
                        />
                        {isSelected && (
                          <path
                            d={`M ${LABEL_PADDING_LEFT + 2.5} ${boxY + 5.5} l 2 2.5 l 4 -5`}
                            fill="none"
                            stroke="#fff"
                            strokeWidth={1.6}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            pointerEvents="none"
                          />
                        )}
                        <text
                          x={batchLabelX}
                          y={row.y + row.height / 2 + 4}
                          fill={isHovered || isSelected ? "#fff" : "#e8e6e3"}
                          fontSize="11"
                          fontWeight={isHovered || isSelected ? "600" : "400"}
                          clipPath={`url(#${labelClipId})`}
                        >
                          <title>{rowBatch.MixCode}</title>
                          {fitSvgText(row.label, batchTextWidth, BATCH_LABEL_FONT)}
                        </text>
                      </>
                    ) : (
                      <>
                        <text
                          x={LABEL_PADDING_LEFT}
                          y={row.y + row.height / 2 + 5}
                          fill="#e8e6e3"
                          fontSize="15"
                          fontWeight="600"
                          clipPath={`url(#${labelClipId})`}
                        >
                          <title>{row.label}</title>
                          {fitSvgText(row.label, labelTextWidth, LANE_LABEL_FONT)}
                        </text>
                        <line
                          x1={0}
                          y1={row.y + row.height}
                          x2={chartWidth}
                          y2={row.y + row.height}
                          stroke="rgba(255,255,255,0.06)"
                          strokeWidth={1}
                        />
                      </>
                    )}

                    {row.bars.map((bar, barIdx) => {
                      const barX = getBarX(bar.start);
                      const barW = Math.max(getBarWidth(bar.start, bar.end), 2);
                      // Bars sharing a window sit on their own sub-lane: a
                      // parallel phase group on a batch row, two batches
                      // contending for the same machine on a resource row.
                      const barY = row.y + ROW_PADDING + bar.subLane * (BAR_HEIGHT + BAR_GAP);

                      // On a resource row the bar itself is the batch, so it
                      // takes over hover, selection and the identifying label.
                      const barBatch = bar.batch;
                      const barSelected = isSelected || selectedSet.has(barBatch.ID);
                      const barHovered = isHovered || hoveredBatch === barBatch.MixCode;
                      const barText = rowBatch
                        ? bar.label
                        : fitSvgText(barBatch.MixCode ?? "", barW - 8, BAR_TEXT_FONT);

                      return (
                        <g
                          key={`${bar.code}-${barBatch.ID}-${barIdx}`}
                          onMouseMove={(e) => {
                            showPhaseDetails(bar, e);
                            if (!rowBatch) setHoveredBatch(barBatch.MixCode);
                          }}
                          onMouseLeave={() => {
                            setHoveredPhase(null);
                            if (!rowBatch) setHoveredBatch(null);
                          }}
                          onClick={rowBatch ? undefined : () => toggleSelection(barBatch.ID)}
                        >
                          <rect
                            x={barX}
                            y={barY}
                            width={barW}
                            height={BAR_HEIGHT}
                            fill={bar.color}
                            rx={3}
                            opacity={barHovered || barSelected ? 1 : 0.85}
                            stroke={barSelected ? SELECTION_COLOR : "none"}
                            strokeWidth={barSelected ? 1.5 : 0}
                            style={{ cursor: "pointer" }}
                          />
                          {barW > 30 && barText.length > 1 && (
                            <text
                              x={rowBatch ? barX + barW / 2 : barX + 4}
                              y={barY + BAR_HEIGHT / 2 + 4}
                              textAnchor={rowBatch ? "middle" : "start"}
                              fill="#fff"
                              fontSize="9"
                              fontWeight="600"
                              pointerEvents="none"
                            >
                              {barText}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {isSelected && (
                      <rect
                        x={0}
                        y={row.y + 0.5}
                        width={chartWidth}
                        height={row.height - 1}
                        fill="none"
                        stroke={SELECTION_COLOR}
                        strokeWidth={1}
                        pointerEvents="none"
                      />
                    )}
                  </g>
                );
              })}
            </g>
          ))}
        </svg>
      </div>

      {hoveredPhase &&
        (() => {
          const { bar, x, y } = hoveredPhase;
          const { batch, phase } = bar;
          const name = PHASE_META[bar.code]?.name
            ? t(PHASE_META[bar.code].name)
            : phase.name
            ? t(phase.name)
            : bar.code;
          const machine =
            phase.machineId == null
              ? t("Bez uređaja")
              : machineNames.get(phase.machineId) || t("Uređaj #{id}", { id: phase.machineId });

          // Anchored to the cursor and flipped near the viewport edges, so the
          // details never fall outside the window for a bar on the far right or
          // at the bottom of a long chart.
          const flipX = x + TOOLTIP_OFFSET + TOOLTIP_WIDTH > window.innerWidth;
          const flipY = y + TOOLTIP_OFFSET + TOOLTIP_MAX_HEIGHT > window.innerHeight;

          return (
            <div
              className="gantt-phase-tooltip"
              style={{
                width: TOOLTIP_WIDTH,
                left: flipX ? x - TOOLTIP_OFFSET - TOOLTIP_WIDTH : x + TOOLTIP_OFFSET,
                top: flipY ? undefined : y + TOOLTIP_OFFSET,
                bottom: flipY ? window.innerHeight - y + TOOLTIP_OFFSET : undefined,
              }}
            >
              <div className="tip-title">
                <span className="tip-dot" style={{ background: bar.color }} />
                {name}
              </div>
              <div className="tip-time">
                {formatAbsTime(bar.start)} – {formatAbsTime(bar.end, true)} ·{" "}
                {formatDuration(bar.end - bar.start)}
              </div>
              <dl>
                <dt>{t("Stroj")}</dt>
                <dd>{machine}</dd>
                {phase.workers != null && (
                  <>
                    <dt>{t("Radnici")}</dt>
                    <dd>{phase.workers}</dd>
                  </>
                )}
                <dt>{t("Zamjesa")}</dt>
                <dd>{batch.MixCode}</dd>
                {batch.ProductName && (
                  <>
                    <dt>{t("Proizvod")}</dt>
                    <dd>{batch.ProductName}</dd>
                  </>
                )}
                {batch.FinalMassKg != null && (
                  <>
                    <dt>{t("Masa")}</dt>
                    <dd>{batch.FinalMassKg} kg</dd>
                  </>
                )}
              </dl>
              {phase.parallel && (
                <div className="tip-parallel">{t("Teče paralelno s drugom fazom istog koraka")}</div>
              )}
            </div>
          );
        })()}

      <style>{`
        .gantt-planner-chart {
          background: #1a1d29;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .gantt-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1rem;
          flex-wrap: wrap;
        }

        .gantt-header-left {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .gantt-header-left h3 {
          color: #e8e6e3;
          margin: 0;
        }

        .gantt-group-toggle {
          align-self: flex-start;
        }

        .gantt-hints {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .gantt-drag-hint {
          font-size: 12px;
          color: #95a5a6;
          font-style: italic;
        }

        .gantt-selection-bar {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 0.85rem;
          color: #e8e6e3;
        }

        .gantt-selection-bar button {
          background: transparent;
          border: 1px solid #4a90e2;
          color: #4a90e2;
          border-radius: 4px;
          padding: 0.2rem 0.6rem;
          font-size: 0.8rem;
          cursor: pointer;
        }

        .gantt-selection-bar button:hover {
          background: rgba(74, 144, 226, 0.15);
        }

        .gantt-legend {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          color: #adb3bd;
        }

        .legend-color {
          width: 16px;
          height: 16px;
          border-radius: 3px;
        }

        .gantt-scroll-container {
          overflow-x: auto;
          overflow-y: hidden;
        }

        .gantt-svg {
          display: block;
        }

        .gantt-batch-row {
          transition: all 0.2s;
        }

        .gantt-batch-row rect {
          transition: all 0.2s;
        }

        .gantt-phase-tooltip {
          position: fixed;
          z-index: 60;
          background: #11141d;
          border: 1px solid #3a4152;
          border-radius: 6px;
          padding: 0.6rem 0.7rem;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
          pointer-events: none;
          font-size: 0.78rem;
          line-height: 1.35;
          color: #e8e6e3;
        }

        .gantt-phase-tooltip .tip-title {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .gantt-phase-tooltip .tip-dot {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          flex: none;
        }

        .gantt-phase-tooltip .tip-time {
          color: #adb3bd;
          margin: 0.2rem 0 0.5rem;
        }

        .gantt-phase-tooltip dl {
          display: grid;
          grid-template-columns: auto 1fr;
          gap: 0.2rem 0.6rem;
          margin: 0;
        }

        .gantt-phase-tooltip dt {
          color: #8b919e;
        }

        .gantt-phase-tooltip dd {
          margin: 0;
          overflow-wrap: anywhere;
        }

        .gantt-phase-tooltip .tip-parallel {
          margin-top: 0.45rem;
          color: #b18cd9;
        }
      `}</style>
    </div>
  );
};

export default GanttPlannerChart;
